import { createContext, useState, useEffect, useContext } from 'react';
import { authApi } from '../utils/api';

// Create the auth context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'user' or 'feeder'
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token') || null);
  
  // Initialize auth state from localStorage
  useEffect(() => {
    const checkLoggedIn = async () => {
      if (token) {
        try {
          // Get stored user type
          const storedUserType = localStorage.getItem('userType');
          setUserType(storedUserType);
          
          // Get user profile based on user type
          if (storedUserType === 'feeder') {
            const feederProfile = await authApi.getFeederProfile();
            setCurrentUser(feederProfile);
          } else {
            // Default to regular user
            const userProfile = await authApi.getUserProfile();
            setCurrentUser(userProfile);
          }
        } catch (error) {
          console.error('Error validating token:', error);
          // If token is invalid, clear auth state
          localStorage.removeItem('token');
          localStorage.removeItem('userType');
          setToken(null);
          setCurrentUser(null);
          setUserType(null);
        }
      }
      setLoading(false);
    };
    
    checkLoggedIn();
  }, [token]);
  
  // User login function
  const loginUser = async (email, password) => {
    try {
      const data = await authApi.loginUser(email, password);
      // Extract token from response
      const authToken = data.token;
      
      // Save the token and user type to localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userType', 'user');
      setToken(authToken);
      
      // Get user profile with the new token
      try {
        const userProfile = await authApi.getUserProfile();
        setCurrentUser(userProfile);
      } catch (profileError) {
        console.error('Error fetching user profile:', profileError);
      }
      
      setUserType('user');
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed. Please try again.'
      };
    }
  };
  
  // Feeder login function
  const loginFeeder = async (email, password) => {
    try {
      const data = await authApi.loginFeeder(email, password);
      // Extract token from response
      const authToken = data.token;
      
      // Save the token and user type to localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userType', 'feeder');
      setToken(authToken);
      
      // Get feeder profile with the new token
      try {
        const feederProfile = await authApi.getFeederProfile();
        setCurrentUser(feederProfile);
      } catch (profileError) {
        console.error('Error fetching feeder profile:', profileError);
      }
      
      setUserType('feeder');
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed. Please try again.'
      };
    }
  };
  
  // User registration function
  const registerUser = async (name, email, password) => {
    try {
      const data = await authApi.registerUser(name, email, password);
      // Extract token from response
      const authToken = data.token;
      
      // Save the token and user type to localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userType', 'user');
      setToken(authToken);
      
      // Get user profile with the new token
      try {
        const userProfile = await authApi.getUserProfile();
        setCurrentUser(userProfile);
      } catch (profileError) {
        console.error('Error fetching user profile:', profileError);
      }
      
      setUserType('user');
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };
  
  // Feeder registration function
  const registerFeeder = async (userData) => {
    try {
      const data = await authApi.registerFeeder(userData);
      // Extract token from response
      const authToken = data.token;
      
      // Save the token and user type to localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userType', 'feeder');
      setToken(authToken);
      
      // Get feeder profile with the new token
      try {
        const feederProfile = await authApi.getFeederProfile();
        setCurrentUser(feederProfile);
      } catch (profileError) {
        console.error('Error fetching feeder profile:', profileError);
      }
      
      setUserType('feeder');
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed. Please try again.'
      };
    }
  };
  
  // Google login function - you may need to adapt this for both user types
  const googleLogin = async (googleToken) => {
    try {
      const data = await authApi.googleLogin(googleToken);
      // Extract token from response
      const authToken = data.token;
      const type = data.userType || 'user'; // Default to user if not specified
      
      // Save the token and user type to localStorage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userType', type);
      setToken(authToken);
      setUserType(type);
      
      // Get user profile based on the user type
      try {
        if (type === 'feeder') {
          const feederProfile = await authApi.getFeederProfile();
          setCurrentUser(feederProfile);
        } else {
          const userProfile = await authApi.getUserProfile();
          setCurrentUser(userProfile);
        }
      } catch (profileError) {
        console.error('Error fetching user profile:', profileError);
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Google login failed. Please try again.'
      };
    }
  };
  
  // Logout function
  const logout = () => {
    // Remove the token and user type from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    setToken(null);
    setCurrentUser(null);
    setUserType(null);
  };
  
  // Provide auth context value
  const value = {
    currentUser,
    userType,
    token,
    loading,
    loginUser,
    loginFeeder,
    registerUser,
    registerFeeder,
    googleLogin,
    logout,
    isAuthenticated: !!currentUser
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 